import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Heart, Plus, ThumbsUp, Sparkles } from 'lucide-react';
import { toast } from 'sonner';

export const WhatHelped = () => {
  const { API, token } = useAuth();
  const [strategies, setStrategies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [formData, setFormData] = useState({
    strategy: '',
    category: 'grounding'
  });

  const categories = [
    { value: 'all', label: 'All Strategies' },
    { value: 'grounding', label: 'Grounding & Calming' },
    { value: 'self-care', label: 'Self-Care' },
    { value: 'child-support', label: 'Supporting Your Child' },
    { value: 'community', label: 'Community & Connection' },
    { value: 'medical', label: 'Medical & Treatment' }
  ];

  useEffect(() => {
    fetchStrategies();
  }, [selectedCategory]);

  const fetchStrategies = async () => {
    try {
      setLoading(true);
      const params = selectedCategory !== 'all' ? `?category=${selectedCategory}` : '';
      const response = await axios.get(`${API}/shared-coping${params}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStrategies(response.data);
    } catch (error) {
      console.error('Error fetching strategies:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/shared-coping`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Strategy shared with community!');
      setDialogOpen(false);
      setFormData({ strategy: '', category: 'grounding' });
      fetchStrategies();
    } catch (error) {
      toast.error('Failed to share strategy');
    }
  };

  const markHelpful = async (strategyId) => {
    try {
      await axios.post(`${API}/shared-coping/${strategyId}/helpful`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Marked as helpful!');
      fetchStrategies();
    } catch (error) {
      toast.error('Failed to mark as helpful');
    }
  };

  return (
    <div className="space-y-8" data-testid="what-helped-page">
      {/* Header */}
      <div className="bg-gradient-to-br from-primary/10 to-secondary/20 rounded-xl p-8 md:p-12">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4 mb-4">
              <Sparkles className="w-12 h-12 text-primary" strokeWidth={1.5} />
              <h1 className="text-3xl md:text-4xl font-nunito font-bold text-[#2D2D2D]">
                What Helped
              </h1>
            </div>
            <p className="text-lg text-[#57534E] leading-relaxed">
              Anonymous strategies shared by parents in our community. When something works, share it forward.
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button
                className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-6 font-bold shadow-sm transition-transform hover:scale-[1.02]"
                data-testid="share-strategy-button"
              >
                <Plus className="w-5 h-5 mr-2" />
                Share Strategy
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="text-2xl font-nunito font-bold">Share What Helped You</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                <p className="text-[#57534E]">
                  Your strategy will be shared anonymously. Focus on what actually helped - no matter how small.
                </p>

                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <select
                    id="category"
                    value={formData.category}
                    onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                    className="w-full h-12 px-4 bg-stone-50 border border-stone-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20"
                    data-testid="strategy-category-select"
                  >
                    {categories.slice(1).map(cat => (
                      <option key={cat.value} value={cat.value}>{cat.label}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="strategy">What helped you?</Label>
                  <Textarea
                    id="strategy"
                    value={formData.strategy}
                    onChange={(e) => setFormData({ ...formData, strategy: e.target.value })}
                    placeholder="Example: Taking a 5-minute walk helped me reset when feeling overwhelmed..."
                    rows={6}
                    required
                    data-testid="strategy-textarea"
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full bg-primary text-white hover:bg-primary/90 rounded-full h-12 font-bold"
                  data-testid="submit-strategy-button"
                >
                  Share Anonymously
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-3">
        {categories.map(cat => (
          <button
            key={cat.value}
            onClick={() => setSelectedCategory(cat.value)}
            className={`px-6 py-3 rounded-full font-semibold transition-all duration-300 ${
              selectedCategory === cat.value
                ? 'bg-primary text-white shadow-md'
                : 'bg-white text-[#57534E] border border-stone-200 hover:border-primary'
            }`}
            data-testid={`category-${cat.value}`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Strategies List */}
      {loading ? (
        <div className="text-center py-12">
          <p className="text-[#A8A29E]">Loading strategies...</p>
        </div>
      ) : strategies.length === 0 ? (
        <Card className="bg-white rounded-xl border border-stone-100 p-12 text-center">
          <Sparkles className="w-16 h-16 text-[#A8A29E] mx-auto mb-4" />
          <p className="text-[#57534E] text-lg mb-4">Be the first to share in this category</p>
          <Button
            onClick={() => setDialogOpen(true)}
            className="bg-primary text-white hover:bg-primary/90 rounded-full px-6 py-3"
          >
            Share What Helped
          </Button>
        </Card>
      ) : (
        <div className="grid gap-6">
          {strategies.map((strategy) => (
            <Card
              key={strategy.id}
              className="bg-white rounded-xl border border-stone-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 space-y-3">
                  <div className="flex items-center gap-2">
                    <span className={`px-4 py-1 rounded-full text-sm font-semibold ${
                      strategy.category === 'grounding' ? 'bg-primary/20 text-primary' :
                      strategy.category === 'self-care' ? 'bg-secondary text-secondary-foreground' :
                      strategy.category === 'child-support' ? 'bg-accent/20 text-accent' :
                      strategy.category === 'community' ? 'bg-primary/10 text-primary' :
                      'bg-stone-100 text-[#57534E]'
                    }`}>
                      {categories.find(c => c.value === strategy.category)?.label}
                    </span>
                  </div>
                  <p className="text-[#2D2D2D] leading-relaxed whitespace-pre-wrap">
                    {strategy.strategy}
                  </p>
                  <div className="flex items-center gap-4 pt-2">
                    <button
                      onClick={() => markHelpful(strategy.id)}
                      className="flex items-center gap-2 text-primary hover:text-primary/80 transition-colors"
                      data-testid={`helpful-${strategy.id}`}
                    >
                      <ThumbsUp className="w-4 h-4" />
                      <span className="text-sm font-medium">
                        {strategy.helpful_count > 0 && `${strategy.helpful_count} found this helpful`}
                        {strategy.helpful_count === 0 && 'Mark as helpful'}
                      </span>
                    </button>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Footer Message */}
      <Card className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl border border-stone-100 p-6 text-center">
        <p className="text-[#57534E] leading-relaxed">
          <Heart className="w-5 h-5 inline text-primary fill-primary mr-2" />
          Every strategy shared here represents real experience from parents navigating this journey. 
          What works for one family might spark an idea for another.
        </p>
      </Card>
    </div>
  );
};

export default WhatHelped;
